
import { Options, Vue, mixins } from "vue-class-component";
import PosService from "../../service/PosService.js";
import ProfilerService from "../../service/ProfilerService.js";
import ChartService from "../../service/ChartService.js";
import useVuelidate from "@vuelidate/core";
import {
    required,
    requiredIf,
    helpers,
    maxLength,
} from "@vuelidate/validators";
import Toaster from "../../helpers/Toaster";
import moment from "moment";
import AutoComplete from "primevue/autocomplete";
import SearchFilter from "../../components/SearchFilter.vue";
import PreviewAccountingReceipt from "../../components/PreviewAccountingReceipt.vue";
import { ref, defineComponent, toRefs, reactive } from "vue";
import PaymentScreen from "../../components/PaymentScreen.vue";
import { useStore, ActionTypes } from "../../store";
import ProfilerDialog from "../../components/ProfilerDialog.vue";
import { ItemList } from "../pos_receipt/IPosReceipt";
import router from "../../router";
import Chips from "primevue/chips";
import CashierSession from "../../service/CashierSession";
import UtilityOptions from "../../mixins/UtilityOptions";
import BranchValidator from "../../utils/branchValidator.js";
import FooterService from "../../service/footerService.js";
import CartService from "../../service/CartService.js";

interface PaymentListType {
    paymentType: string;
    accountNo: string;
    terminalId: string;
    authCode: string;
    transId: string;
    transStatus: string;
    transType: string;
    transDate: string;
    transTime: string;
    transAmount: number;
    transTotalAmount: number;
    transRef: string;
    entryMode: string;
    hostResponse: string;
    giftCardRef: string;
    cardBalance: string;
    tendered: number;
    change: number;
    roundOff: number;
}

interface CounterEntry {
    accountID: number;
    accountHead: string;
    amount: number;
    type: string;
}

@Options({
    title: "Orders",
    components: {
        AutoComplete,
        SearchFilter,
        PreviewAccountingReceipt,
        PaymentScreen,
        ProfilerDialog,
        Chips,
    },

    //adding this option to make the unit price calculation at the item select event
    watch: {
        savedItemList: {
            handler(newList) {
                // Find the newly added item
                const newItem = newList.find(
                    (item) =>
                        !this.previousItemList.some(
                            (prevItem) => prevItem.stockID === item.stockID
                        )
                );
                if (newItem) {
                    this.setPricing(newItem);
                }

                // Update previousItemList
                this.previousItemList = [...newList];

                if (newItem) {
                    setTimeout(() => {
                        // need to focus the Unit Qty here
                        this.focusNext(
                            "item-unit-" + newItem.rowID,
                            null,
                            null
                        );
                    }, 100);
                }
            },
            deep: true, // Ensures that changes in nested properties are also detected
        },
        "state.chippedTel": {
            handler(newVal) {
                if (this.isLoadingCart) return; 
                if (newVal.length === 0) {
                    this.triggerDelayedTel(true);
                } else {
                    this.delayedChippedTel = false;
                }
            },
            immediate: true,
            deep: true,
        },
    },
})
export default class PosReceipt extends mixins(UtilityOptions) {
    private modeList = [
        { label: "REFUND", value: "RFD" },
        { label: "SALE", value: "INE" },
        // { label: "TRANSFER", value: "TRN" },
    ];
    private unitType = ["Pack", "Strip", "Unit"];
    private discountType = [
        { key: 2, name: "Flat" },
        { key: 1, name: "Percentage" },
    ];
    private invoiceDiscount = 0;
    private invoiceDiscountInput = "0";
    private discType = "Flat";
    private paymentDialog = false;
    private profileStatus = false;
    private statusType = "New";
    private storeName = "Loading...";
    private branchId = null;
    private branchName = "";
    private removeProfileDialog = false;
    private submitted = false;
    private defaultCustomerName = "";
    private defaultCustomerID = 0;
    private currentUserID = 0;
    private itemScanBox = "";
    private dialogTitle = "";
    private screenMode = "minimize";
    private profilerService;
    private posService;
    private toast;
    private storeList = [];
    private profilerList = [];
    private itemList = [];
    private isToggled = "";
    private delayedChippedTel: boolean = false;
    private cashPaymentOption = true;
    private cardPaymentOption = true;
    private bankTransferPaymentOption = true;
    private payLaterOption = true;
    private validationPopupMessage =
        "Please fill all the required fields to proceed";
    private validationPopup = false;
    private profileTypes = [{ key: "Customer" }];
    private cashierService;
    private firstElement;

    private counterEntry: CounterEntry[] = [];

    private screenState = reactive({
        fullscreen: false,
        teleport: true,
        pageOnly: false,
    });

    private selectedTaxes = {
        tax1: false,
        tax2: false,
        tax3: false,
    };

    private taxNames = [
        {
            taxName: "",
            show: false,
            optionalReq: "",
            taxValue: 0,
            accountHead: "",
            accountID: 0,
        },
        {
            taxName: "",
            show: false,
            optionalReq: "",
            taxValue: 0,
            accountHead: "",
            accountID: 0,
        },
        {
            taxName: "",
            show: false,
            optionalReq: "",
            taxValue: 0,
            accountHead: "",
            accountID: 0,
        },
    ];

    private state = reactive({
        selectedProfile: "",
        chippedProfile: [] as string[],
        customerTel: "",
        chippedTel: [] as string[],
        transferStoreID: "",
    });

    private savedItemList: ItemList[] = [];
    private savedItemListAll: any[] = [];
    private previousItemList: ItemList[] = [];

    private telBlurred: boolean = false;

    private validName(value: string): boolean {
        return /^[A-Za-z\s]+$/.test(value);
    }

    onTelBlur() {
        this.telBlurred = true;
        (this.v$ as any).customerTel.$touch();
    }

    private validationRules = {
        selectedProfile: {
            required: helpers.withMessage(
                "Customer Name is required",
                requiredIf(() => {
                    return this.state.chippedProfile.length === 0;
                })
            ),

            validName: helpers.withMessage(
                "Only letters are allowed",
                (value: unknown) => {
                    if (this.state.chippedProfile.length > 0) return true;
                    if (!value) return true;
                    if (typeof value !== "string") return false;
                    return this.validName(value);
                }
            ),
        },
        customerTel: {
            invalid: helpers.withMessage(
                "Invalid Phone Number",
                helpers.withParams(
                    { type: 'invalid' },
                    (value: unknown): boolean => {
                        if (!value || typeof value !== 'string' || value.trim().length === 0) {
                            return true;
                        }
                        let raw = value.trim().replace(/[\s\-()+]/g, '');

                        if (raw.startsWith('94') && raw.length === 11) {
                            raw = raw.slice(2);       
                        } else if (raw.startsWith('0') && raw.length === 10) {
                            raw = raw.slice(1);       
                        }

                        if (!/^\d{9}$/.test(raw)) return false;

                        const prefix2 = raw.slice(0, 2);

                        const mobilePrefixes = ['70', '71', '72', '74', '75', '76', '77', '78'];
                        if (mobilePrefixes.includes(prefix2)) return true;

                        const landlinePrefixes = [
                            '11', '31', '33', '34', '36', '38', '81', '52', '54', '51', '66',
                            '91', '41', '47', '21', '23', '24', '65', '26', '63', '67', '37',
                            '32', '25', '27', '55', '57', '45', '35',
                        ];
                        if (landlinePrefixes.includes(prefix2)) return true;
                        return false;
                    }
                )
            ),
        },
        transferStoreID: {
            required: helpers.withMessage(
                "Transfer Store is required",
                requiredIf(() => {
                    return this.item.type == "TRN";
                })
            ),
        },
    };

    private paymentList: PaymentListType[] = [];

    private item = {
        id: 0,
        profileID: this.defaultCustomerID,
        discount: 0,
        totalPaid: 0,
        totalTendered: 0,
        totalChange: 0,
        totalGrossAmt: 0,
        invoiceDiscount: 0,
        discType: "",
        totalCost: 0,
        totalBill: 0,
        totalTax1: 0,
        totalTax2: 0,
        totalTax3: 0,
        totalTax: 0,
        description: "",
        paymentMethod: "",
        doctorDetails: "",
        patientDetails: "",
        searchReceiptNo: "",
        status: "Active",
        type: "INE",
    };

    private v$ = useVuelidate(this.validationRules, this.state);

    private cartService;
    private activeCartId: number | null = null;
    private editCartId: number | null = null;
    private isEditMode = false;
    private adjustedItems: string[] = []; 
    private isLoadingCart = false;
    private draftCartId: number | null = null;
    private draftSyncTimer: any = null;
    private _beaconHandler: () => void = () => {};
    private _visibilityHandler: () => void = () => {};

    //DEFAULT METHOD OF TYPE SCRIPT
    created() {
        this.profilerService = new ProfilerService();
        this.posService = new PosService();
        this.toast = new Toaster();
        this.cashierService = new CashierSession();
        this.cartService = new CartService();
    }

    selectAll(event) {
        event.target.select(); // selects all text on focus
    }

    mounted() {
        this.checkPaymentPermission();
        this.loadList().then(() => {
            this.$nextTick(() => {
                const cartIdParam = this.$route?.query?.cart_id;
                const modeParam   = this.$route?.query?.mode;
                if (cartIdParam && modeParam === 'edit') {
                    this.editCartId = Number(cartIdParam);
                    this.isEditMode = true;
                    this.loadCartForEdit(this.editCartId);
                }
            });
        });
        localStorage.setItem("pos_items", "0");
        
        // Fetch and store current branch on page load to ensure validation works from first search
        const footerService = new FooterService();
        footerService.branchDetail().then((res) => {
            if (res.store_info) {
                BranchValidator.storeTransactionBranch(
                    res.store_info.id,
                    res.store_info.name,
                    'POS',
                    res.store_info.pharmacy?.name || ''
                );
            }
        });

        window.addEventListener("keypress", this.handleKeypress);
        window.addEventListener("keydown", this.handleKeydown);
        this.focusNext("pos-input", null, null);

        window.addEventListener('beforeunload', function (e) {
            // Check your localStorage flags
            const pos_item_list = localStorage.getItem('pos_items');
            if (pos_item_list == '1') {
                // Standard message (modern browsers ignore custom text)
                e.preventDefault();
                e.returnValue = '';
                // Some browsers require returnValue to be set
                return '';
            }
        });

        this._beaconHandler = () => {
            if (this.isEditMode && this.editCartId) {
                const url = '/beacon/revert_token';
                const data = JSON.stringify({ cart_id: this.editCartId });
                navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
            }
        };

        this._visibilityHandler = () => {
            if (document.visibilityState === 'visible') {
            } 
        };

        window.addEventListener('pagehide', this._beaconHandler);
        window.addEventListener('beforeunload', this._beaconHandler);
        window.addEventListener('visibilitychange', this._visibilityHandler);

    }

    filterNumbers(event) {
        const input = event.target as HTMLInputElement;
        input.value = input.value.replace(/[^0-9]/g, '');
    }

    filterAlphaNumeric(e) {
        const input = e.target as HTMLInputElement;
      // Keep only A-Z, a-z, 0-9
      input.value = input.value.replace(/[^a-zA-Z0-9.\-\s]/g, '');
    }

    get invoiceDiscountMax() {
        if (this.discType === "Flat") {
            const available = Number(
                (this.totalGross - this.itemDiscAmount).toFixed(2)
            );
            return available > 0 ? available : 0;
        }

        return 100;
    }

    handleInvoiceDiscountInput(event: Event) {
        const inputEl = event.target as HTMLInputElement | null;
        if (!inputEl) {
            return;
        }

        let sanitized = inputEl.value.replace(/[^0-9.]/g, "");
        const parts = sanitized.split(".");
        if (parts.length > 2) {
            sanitized = `${parts[0]}.${parts.slice(1).join("")}`;
        }

        if (sanitized.startsWith(".")) {
            sanitized = `0${sanitized}`;
        }

        if (sanitized === "") {
            sanitized = "0";
        }

        const numericValue = Number(sanitized);
        if (isNaN(numericValue)) {
            this.invoiceDiscount = 0;
            this.invoiceDiscountInput = "0";
        } else {
            const limitedValue = Math.min(numericValue, this.invoiceDiscountMax);
            this.invoiceDiscount = limitedValue;
            this.invoiceDiscountInput = limitedValue.toString();
        }

        inputEl.value = this.invoiceDiscountInput;
        // Trigger recalculation
        void this.totalDiscAmount;
        this.scheduleDraftSync();
    }

    handleDiscTypeKeydown(event: KeyboardEvent) {
        const key = event.key;
        const withModifier = event.ctrlKey || event.altKey;

        if (!withModifier) {
            if (key === "ArrowLeft") {
                event.preventDefault();
                this.focusNext("custReceiptDesc", null, null);
                return;
            }

            if (key === "ArrowRight") {
                event.preventDefault();
                this.focusNext("custInvoiceDiscVal", null, null);
                return;
            }

            if (key === "Enter") {
                event.preventDefault();
                this.focusNext("custInvoiceDiscVal", null, null);
                return;
            }
        } else {
            if (key === "ArrowUp") {
                event.preventDefault();
                this.focusNext("custReceiptDesc", null, null);
                return;
            }

            if (key === "ArrowDown") {
                event.preventDefault();
                this.focusNext("custInvoiceDiscVal", null, null);
            }
        }
    }

    clampInvoiceDiscount(event: Event) {
        const inputEl = event.target as HTMLInputElement | null;

        let value = Number(this.invoiceDiscountInput);
        if (isNaN(value) || value < 0) {
            value = 0;
        }

        value = Math.min(value, this.invoiceDiscountMax);

        this.invoiceDiscount = Number(value.toFixed(2));
        this.invoiceDiscountInput = this.invoiceDiscount.toString();

        if (inputEl) {
            inputEl.value = this.invoiceDiscountInput;
        }

        // Trigger recalculation
        void this.totalDiscAmount;
    }

    focusNext(refName, event, special) {
        console.log('special:' + special + ' refName:' + refName);
        if(!special){
            // Select text when focusing quantity inputs
            if (typeof refName === 'string' && (refName.startsWith('item-unit-') || refName.startsWith('item-disc-'))) {
                console.log('Selection QTY and DISC');
                setTimeout(() => {
                    const input = document.getElementById(refName);
                    if (input) {
                        (input as HTMLInputElement).select();
                    }
                }, 10);
            }
        }
        const shiftDropdown = (this.$refs.shiftPosDropdown ?? null) as any;
        var tempElement = document.getElementById(refName);
        if (special) {
            // for special cases
            if (special == "back_to_item_search") {
                // this if for Item menu
                if (!tempElement) {
                    tempElement = document.getElementById("pos-input");
                }
            } else if (special == "back_to_item_customer") {
                // this if for Item menu
                if (!tempElement) {
                    tempElement = document.getElementById("custPhone");
                }
                if (!tempElement) {
                    tempElement = document.getElementById("custDesc");
                }
            } else if (special == "search_bar") {
                const wrapper = document.getElementById("pos-input");
                if (wrapper) {
                    const span = wrapper.closest("span");
                    if (span && span.getAttribute("aria-expanded") === "true") {
                        console.log('Dropdown is visible');
                        return;
                    }
                }
            } else if(special == 'search_bar_right'){
                console.log(this.state.chippedProfile +'|' +this.state.chippedProfile.length);
                const wrapper = document.getElementById("pos-input");
                if (wrapper) {
                        console.log('wrapper');
                    const span = wrapper.closest("span");
                    if (span && span.getAttribute("aria-expanded") === "true") {
                        console.log('Dropdown is visible');
                        return;
                    }
                } 
                if (refName === "shiftPosBtn") {
                        console.log('shiftPosBtn');
                    if (shiftDropdown && typeof shiftDropdown.focus === "function") {

                        shiftDropdown.focus();
                        return;
                    }
                    tempElement = document.getElementById(refName);
                }
                if(this.state.chippedProfile && this.state.chippedProfile.length > 0){
                        console.log('custDesc ');
                    refName = 'custDesc';
                    tempElement = document.getElementById(refName);
                }
            } else if(special== "reg_user"){
                if(this.state.chippedProfile && this.state.chippedProfile.length > 0){
                    refName = 'pos-input';
                    tempElement = document.getElementById(refName);
                }
            } else if (special == "cust_phone_dropdown") {
                const input = document.getElementById("custPhone");
                const inputName = document.getElementById("custName");
                if (input) {
                    const span = input.closest("span");
                    if (span && span.getAttribute("aria-expanded") === "true") {
                        return;
                    }
                }
                if(inputName){
                    const spanName = inputName.closest("span");
                    if (spanName && spanName.getAttribute("aria-expanded") === "true") {
                        return;
                    }
                }
            }

            if (special != "seatch_bar") {
                if (event) {
                    event.preventDefault();
                }
            }
        }

        if (tempElement) {
            const targetElement = tempElement;

            window.setTimeout(() => {
                const active = document.activeElement as HTMLElement | null;
                if (active && active !== targetElement) {
                    active.blur();
                }

                targetElement.focus();

                if (document.activeElement !== targetElement) {
                    const focusableChild = targetElement.querySelector(
                        'input,button,textarea,select,[tabindex]:not([tabindex="-1"]),[role="combobox"]'
                    ) as HTMLElement | null;
                    if (focusableChild) {
                        focusableChild.focus();
                    }
                }
            }, 0);
        }
    }

    handleItemScanBoxRight(event) {
        const input = event.target as HTMLInputElement;
        const cursorPosition = input.selectionStart??100;
        const textLength = input.value.length;
        
        // If cursor is at the end or there are no characters to the right, move to Customer Name
        if (cursorPosition >= textLength) {
            this.focusNext('custName', event, 'search_bar_right');
        } else {
            // Otherwise, move cursor one position to the right
            event.preventDefault();
            input.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        }
    }

    beforeUnmount() {
        window.removeEventListener('keypress', this.handleKeypress);
        window.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('pagehide', this._beaconHandler);
        window.removeEventListener('beforeunload', this._beaconHandler);
        window.removeEventListener('visibilitychange', this._visibilityHandler);
        clearTimeout(this.draftSyncTimer);

        if (this.isEditMode && this.editCartId) {
            this.cartService.revertToTokenised(this.editCartId);
        }
        this.discardCurrentDraft();
    }

    handleKeydown(event) {
        console.log('key down'+ event.key + '|' + event.keyCode);
        // Handle special keys that need keydown event
        if (!this.paymentDialog && !this.profileStatus && !this.validationPopup) {
            // Escape key - Focus HOME button
            if (event.key === "Escape") {
                event.preventDefault();
                this.focusNext("homeBtn", null, null);
                return;
            }
            // Delete key - Clear screen
            if (event.keyCode == 46) {
                event.preventDefault();
                this.clearAllManually();
                return;
            }
            // Insert key - New customer
            if (event.key === "Insert") {
                event.preventDefault();
                this.openProfileDialog();
                return;
            }
            // End key - Logout cashier
            if (event.key === "End") {
                event.preventDefault();
                this.logoutCashier();
                return;
            }
            // F11 key - Toggle fullscreen
            if (event.key === "F11") {
                event.preventDefault();
                this.toggleScreen();
                return;
            }
            if(event.key === "/"){
                event.preventDefault();  
                const profileInfo = {value: this.item.profileID};
                this.removeProfile(profileInfo);
                return;
            }
        } else if (this.validationPopup) {
            // Escape key - Close validation popup
            if (event.key === "Escape") {
                event.preventDefault();
                this.closeValidationPopup();
                return;
            }
        }
    }

    handleKeypress(event) {
        if (!this.paymentDialog && !this.profileStatus && !this.validationPopup) {
            console.log('Key pressed:', event.key, 'KeyCode:', event.keyCode);
            if (event.keyCode == 63) {
                // Key "?"
                event.preventDefault();
                this.focusNext("custPhone", null, null);
                return "";
            }
            if (event.keyCode == 42) {
                // Key "*" - Proceed to payment/next
                event.preventDefault();
                const el = document.getElementById("nextBtn");
                if (el instanceof HTMLButtonElement && !el.disabled) {
                    if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                    }
                    el.click();
                } else {
                    this.validationPopupMessage =
                        "Please fill the required fields and proceed";
                    this.validationPopup = true;
                    setTimeout(() => {
                        this.focusNext("closePopup", null, null);
                    }, 100);
                }
                return "";
            }
            if (event.keyCode === 43) {
                // Key "+" - Focus search bar
                event.preventDefault();
                this.focusNext("pos-input", null, null);
                return "";
            }
            if(event.keyCode === 94){
                // Key "^" - New customer
                event.preventDefault();
                this.openProfileDialog();
                return "";
            }
            if (event.keyCode === 124) {
                // Key "|" - Go to POS-Refund
                router.push({ path: "/pos-refund" });
                return "";
            }
        } else if (this.profileStatus){
            if (event.keyCode === 43) {
                // Key "+" - Save customer in dialog
                event.preventDefault();
                this.focusNext("saveCustomer", null, null);
                return "";
            }
        }
    }

    closeValidationPopup() {
        this.validationPopup = false;
    }

    // Trigger the 'ArrowDown' key to select the first item
    triggerKeyPress(key: string, input: HTMLInputElement) {
        const event = new KeyboardEvent("keypress", {
            key: key,
            code: key,
            bubbles: true,
            cancelable: true,
        });

        setTimeout(() => {
            input.dispatchEvent(event); // Dispatch the key event on the input
        }, 1000);
    }

    toggleScreen() {
        this.screenState.fullscreen = !this.screenState.fullscreen;
    }

    get progressBar() {
        return this.store.getters.getProgressBar;
    }

    searchProfiler(event) {
        setTimeout(() => {
            let param = event.query.trim();
            if (param && param.startsWith("0")) {
                param = param.replace(/^0+/, "");
            }
            this.profilerService
                .searchProfiler(param, "Customer")
                .then((data) => {
                    this.profilerList = data.records;
                    data.records.forEach((record) => {
                        if (
                            record.contact_no === param &&
                            data.records.length === 1
                        ) {
                            const event = { value: data.records[0] };
                            this.saveProfile(event);
                        }
                    });
                });
        }, 200);
    }

    searchItem(event) {
        setTimeout(() => {
            this.posService.searchPosItem(event.query.trim()).then((data) => {

                if (data.alert === 'frozen') {
                    this.validationPopupMessage = data.msg || 'This item is currently frozen and cannot be added.';
                    this.validationPopup = true;
                    this.itemList = [];
                    return;
                }
                // Check for branch mismatch if there's an active transaction
                const storedBranch = BranchValidator.getStoredTransactionBranch();
                if (storedBranch && parseInt(data.current_branch_id) !== parseInt(storedBranch.branchId)) {
                    const storedPharmacy = storedBranch.pharmacyName ? ` (${storedBranch.pharmacyName})` : '';
                    const currentPharmacy = data.current_pharmacy_name ? ` (${data.current_pharmacy_name})` : '';
                    this.toast.showError(`Branch mismatch! Transaction started in "${storedBranch.branchName}"${storedPharmacy} but you're currently in "${data.current_branch_name}"${currentPharmacy}. Please switch back to "${storedBranch.branchName}"${storedPharmacy} branch. If you want to discard this transaction, refresh the page to start fresh.`);
                    this.itemList = [];
                    return;
                }
                
                if (data.records.length == 0) {
                    this.itemList = [];
                    this.toast.showInfo("No Item Found");
                } else {
                    this.itemList = data.records;

                    this.focusNext("pos-input", null, null);
                }
            });
        }, 200);
    }

   saveProfile(event) {
        const profileInfo = event.value;
        this.state.selectedProfile = profileInfo.account_title;
        this.state.customerTel = profileInfo.contact_no.replace(/^0+/, "");

        this.state.chippedTel = [this.state.customerTel];
        this.state.chippedProfile = [profileInfo.account_title];

        this.item.profileID = profileInfo.id;
        this.focusNext('custDesc', null, null);
        this.scheduleDraftSync();
    }

    triggerDelayedTel(value) {
        setTimeout(() => {
            this.delayedChippedTel = value;
        }, 20); // Adjust the delay as needed (200ms here)
    }

    saveItem(event) {
        const itemInfo = event.value;

        if (itemInfo.alert === 'frozen') {
            this.validationPopupMessage = itemInfo.msg || 'This item is currently frozen and cannot be added.';
            this.validationPopup = true;
            this.itemScanBox = '';
            return;
        }

        let sellRate = 0;
        let disc = 0;

        if (this.item.type != "TRN") {
            sellRate = itemInfo.sale_price;
            disc = itemInfo.discount_percentage;
        } else {
            sellRate = itemInfo.purchase_price;
            disc = 0;
        }

        var batchExists = this.savedItemList.find(
            (batch) => batch.stockID == itemInfo.id
        );

        if (!batchExists) {
            // Store branch information when first item is added
            if (this.savedItemList.length === 0) {
                BranchValidator.storeTransactionBranch(
                    this.branchId,
                    this.branchName,
                    this.item.type || 'POS'
                );
            }
            
            var rowID = this.savedItemListAll.length + 1;
            this.savedItemList.push({
                rowID: rowID, //this.savedItemList.length + 1,
                mode: "Unit",
                stockID: itemInfo.id,
                productID: itemInfo.product_id,
                productName: itemInfo.product_name,
                generic: itemInfo.generic_name,
                itemDescription: itemInfo.description,
                unit: 1,
                totalUnit: 0,
                stockQty: Number(itemInfo.qty),
                freeUnit: 0,
                supplierBonus: 0,
                batchNo: itemInfo.batch_no,
                packSize: Number(itemInfo.pack_size),
                sheetSize: Number(itemInfo.strip_size),
                purchasePrice: Number(itemInfo.purchase_price),
                originalPPrice: Number(itemInfo.purchase_price),
                originalSPrice: Number(sellRate),
                sellingPrice: Number(sellRate),
                mrp: Number(itemInfo.mrp),
                brandName: itemInfo.bName,
                sectorName: itemInfo.bSector,
                categoryName: itemInfo.cName,
                productType: itemInfo.pType,
                expiryDate: itemInfo.expiry_date,
                itemDisc: Number(disc),
                tax1: Number(itemInfo.tax_1),
                tax2: Number(itemInfo.tax_2),
                tax3: Number(itemInfo.tax_3),
                subTotal: 0,
            });

            var dataForAll = {
                active:1,
                rowID:rowID
            }
            this.savedItemListAll.push(dataForAll);

            localStorage.setItem("pos_items", "1");
        } else if (batchExists) {
            batchExists.unit = batchExists.unit + 1;
            this.scheduleDraftSync();
        } else {
            this.toast.showInfo(" Item already added");
        }
        this.itemScanBox = "";
        this.scheduleDraftSync();
    }

    loadList() {
        return this.posService.getItems().then((data) => {
            // //taxNames
            this.taxNames = [];

            this.storeList = data.stores;

            if (data.storeTaxes && data.storeTaxes[0]) {
                this.taxNames.push({
                    taxName: data.storeTaxes[0].tax_name_1,
                    show: data.storeTaxes[0].show_1 == "true" ? true : false,
                    optionalReq: data.storeTaxes[0].required_optional_1,
                    taxValue:
                        data.storeTaxes[0].show_1 == "true"
                            ? Number(data.storeTaxes[0].tax_value_1)
                            : 0,
                    accountHead: data.storeTaxes[0].tax_name1?.chartName || '',
                    accountID: data.storeTaxes[0].link1,
                });

                this.taxNames.push({
                    taxName: data.storeTaxes[0].tax_name_2,
                    show: data.storeTaxes[0].show_2 == "true" ? true : false,
                    optionalReq: data.storeTaxes[0].required_optional_2,
                    taxValue:
                        data.storeTaxes[0].show_2 == "true"
                            ? Number(data.storeTaxes[0].tax_value_2)
                            : 0,
                    accountHead: data.storeTaxes[0].tax_name2?.chartName || '',
                    accountID: data.storeTaxes[0].link2,
                });

                this.taxNames.push({
                    taxName: data.storeTaxes[0].tax_name_3,
                    show: data.storeTaxes[0].show_3 == "true" ? true : false,
                    optionalReq: data.storeTaxes[0].required_optional_3,
                    taxValue:
                        data.storeTaxes[0].show_3 == "true"
                            ? Number(data.storeTaxes[0].tax_value_3)
                            : 0,
                    accountHead: data.storeTaxes[0].tax_name3?.chartName || '',
                    accountID: data.storeTaxes[0].link3,
                });

                if (
                    this.taxNames[0].optionalReq == "Required" &&
                    this.taxNames[0].show == true
                ) {
                    this.selectedTaxes.tax1 = true;
                }

                if (
                    this.taxNames[1].optionalReq == "Required" &&
                    this.taxNames[1].show == true
                ) {
                    this.selectedTaxes.tax2 = true;
                }

                if (
                    this.taxNames[2].optionalReq == "Required" &&
                    this.taxNames[2].show == true
                ) {
                    this.selectedTaxes.tax3 = true;
                }
            }

            //Default Customer
            this.state.selectedProfile = data.defaultCustomer.account_title;
            this.item.profileID = data.defaultCustomer.id;
            this.defaultCustomerName = data.defaultCustomer.account_title;
            this.defaultCustomerID = data.defaultCustomer.id;
            this.currentUserID = data.currentUserID;
            this.storeName = data.storeName;
            this.branchId = data.branch_id;
            this.branchName = data.branch_name;
        });
    }

    getTheSubtotal(data) {
        let totalUnits = this.sumUnitTotalQty(data);

        if (data.stockQty < totalUnits && this.item.type != "RFD") {
            data.unit = 1;
            data.mode = "Unit";
            data.freeUnit = 0;
            this.toast.showError("Item does not have enough stock quantity");
        }

        const qty = Number(data.unit);
        const price = Number(data.sellingPrice);
        const discount = Number(data.itemDisc);

        const tax1 = this.selectedTaxes.tax1 == true ? Number(data.tax1) : 0;
        const tax2 = this.selectedTaxes.tax2 == true ? Number(data.tax2) : 0;
        const tax3 = this.selectedTaxes.tax3 == true ? Number(data.tax3) : 0;

        const total = qty * price;
        const disAmount = Number(((total / 100) * discount).toFixed(2)); 

        const afterDis = total - disAmount;
        const afterTax = Number(((afterDis / 100) * (tax1 + tax2 + tax3)).toFixed(2));

        const netTotal = afterDis + afterTax;

        data.subTotal = Number(this.fixDigits(netTotal));

        //TOTAL UNITS
        if (data.mode == "Pack") {
            data.totalUnit = data.packSize * data.unit + data.freeUnit;
        } else if (data.mode == "Strip") {
            data.totalUnit = data.sheetSize * data.unit + data.freeUnit;
        } else {
            data.totalUnit = data.unit + data.freeUnit;
        }

        return this.fixDigits(netTotal);
    }

    getTopMostItem() {
        const activeItems = this.savedItemListAll.filter(item => item.active === 1);
        if (activeItems.length === 0) return 0;
        
        // Get the maximum rowID (top-most item)
        const rowIDs = activeItems.map(item => item.rowID);
        return Math.max(...rowIDs);
    }

    getBottomMostItem() {
        const activeItems = this.savedItemListAll.filter(item => item.active === 1);
        if (activeItems.length === 0) return 0;
        
        // Get the minimum rowID (bottom-most item)
        const rowIDs = activeItems.map(item => item.rowID);
        return Math.min(...rowIDs);
    }

    getNextActiveItem(currentRowID) {

        console.log(this.savedItemListAll);
        console.log(this.savedItemListAll.sort((a, b) => a.rowID - b.rowID));
        if (this.savedItemListAll.length === 0) return 0;

        let flagVar = 0;
        for (const element of this.savedItemListAll.sort((a, b) => a.rowID - b.rowID)) {
            if(element.active === 1){
                if(element.rowID == currentRowID){
                    console.log('selected element:');
                    console.log(element);
                    flagVar = 1;
                } else if(flagVar == 1){
                    console.log('next element:');
                    console.log(element);
                    return element.rowID;
                }
            }
        }
    }

    getPreviousActiveItem(currentRowID) {
        console.log(this.savedItemListAll);
        console.log(this.savedItemListAll.sort((a, b) => b.rowID - a.rowID));
        if (this.savedItemListAll.length === 0) return 0;

        let flagVar = 0;
        for (const element of this.savedItemListAll) {
            if(element.active === 1){
                if(element.rowID == currentRowID){
                    console.log('selected element:');
                    console.log(element);
                    flagVar = 1;
                } else if(flagVar == 1){
                    console.log('next element:');
                    console.log(element);
                    return element.rowID;
                }
            }
        }
    }

    //This part prevent unneccessary characters being entered in the qty input field
    validateUnit(savedItem, event) {
        // Get the input value
        let inputValue = event.target.value;
        //console.log(event.target.value);
        // Allow only digits (no decimal points for quantity)
        let numericValue = inputValue.replace(/[^0-9]/g, "");

        // Parse the value and ensure it's a valid number
        let finalValue = isNaN(Number(numericValue)) ? 0 : Number(numericValue);

        if (this.item.type !== "RFD") {
            let maxAllowed: number;
            if (savedItem.mode === 'Pack' && savedItem.packSize > 0) {
                maxAllowed = Math.floor(savedItem.stockQty / savedItem.packSize);
            } else if (savedItem.mode === 'Strip' && savedItem.sheetSize > 0) {
                maxAllowed = Math.floor(savedItem.stockQty / savedItem.sheetSize);
            } else {
                maxAllowed = savedItem.stockQty;
            }

            if (finalValue > maxAllowed) {
                finalValue = maxAllowed;
                this.toast.showError(`Maximum available: ${maxAllowed}`);
            }
        }

        // Update savedItem.unit with valid integer value
        savedItem.unit = finalValue;

        // Update the input field value directly, allowing user to see the current input
        event.target.value = String(finalValue);
        this.scheduleDraftSync();
    }

    //This part prevent unneccessary characters being entered in the qty input field
    validateDisc(savedItem, event) {
        // Get the input value
        let inputValue = event.target.value;

        // Allow digits and a single decimal point
        let numericValue = inputValue.replace(/[^0-9.]/g, "");

        // console.log('Inp:' + inputValue + ' | Numeric: ' + numericValue);

        if (numericValue < 0) {
            numericValue = 0;
        }

        if (numericValue > 100) {
            numericValue = 100;
        }

        // Parse the value and ensure it's a valid number, allowing intermediate "." inputs
        const finalValue = isNaN(Number(numericValue))
            ? numericValue
            : Number(numericValue);
        savedItem.itemDisc = finalValue;

        // Update the input field value directly, allowing user to see the current input
        event.target.value = numericValue;
        this.scheduleDraftSync();
    }

    get totalGross() {
        let total = 0;
        this.savedItemList.forEach((e) => {
            total = total + Number((e.sellingPrice * e.unit).toFixed(2));
        });
        return Number(total.toFixed(2));
    }

    get totalCostInventory() {
        let total = 0;
        this.savedItemList.forEach((e) => {
            total = total + e.purchasePrice * e.unit;
        });

        return total;
    }

    get totalTax1() {
        let total = 0;

        this.savedItemList.forEach((e) => {
            const tax = this.selectedTaxes.tax1 == true ? Number(e.tax1) : 0;
            const price = e.sellingPrice * e.unit;
            const afterDisc = (price / 100) * e.itemDisc;
            const lineTax = Number((((price - afterDisc) / 100) * tax).toFixed(2));
            total = total + lineTax;
        });

        return Number(total.toFixed(2));
    }

    get totalTax2() {
        let total = 0;
        this.savedItemList.forEach((e) => {
            const tax = this.selectedTaxes.tax2 == true ? Number(e.tax2) : 0;
            const price = e.sellingPrice * e.unit;
            const afterDisc = (price / 100) * e.itemDisc;
            const lineTax = Number((((price - afterDisc) / 100) * tax).toFixed(2));
            total = total + lineTax;
        });

        return Number(total.toFixed(2));
    }

    get totalTax3() {
        let total = 0;
        this.savedItemList.forEach((e) => {
            const tax = this.selectedTaxes.tax3 == true ? Number(e.tax3) : 0;
            const price = e.sellingPrice * e.unit;
            const afterDisc = (price / 100) * e.itemDisc;
            const lineTax = Number((((price - afterDisc) / 100) * tax).toFixed(2));
            total = total + lineTax;
        });

        return Number(total.toFixed(2));
    }

    get totalDiscAmount() {
        const total = this.itemDiscAmount + this.extraDiscAmount;
        return Number(total.toFixed(2));
    }

    get itemDiscAmount() {
        let itemtotal = 0;
        this.savedItemList.forEach((e) => {
            const price = e.sellingPrice * e.unit;
            const lineDisc = Number(((price / 100) * e.itemDisc).toFixed(2));
            itemtotal = itemtotal + lineDisc;
        });
        return Number(itemtotal.toFixed(2));
    }

    get extraDiscAmount() {
        let extraDiscVal = 0;
        const itemtotal = this.itemDiscAmount;
        const invoiceDiscount = Number(this.invoiceDiscount) || 0;
        if (this.discType == "Flat") {
            extraDiscVal = invoiceDiscount;
        } else {
            extraDiscVal = ((this.totalGross - itemtotal) / 100) * invoiceDiscount;
        }
        return Number(extraDiscVal.toFixed(2));
    }

    get netTotal() {
        return Number(
            (
                this.totalGross -
                this.totalDiscAmount +
                this.totalTax1 +
                this.totalTax2 +
                this.totalTax3
            ).toFixed(2)
        );
    }

    get netTotalBeforDisc() {
        return Number(
            (
                this.totalGross +
                this.totalTax1 +
                this.totalTax2 +
                this.totalTax3
            ).toFixed(2)
        );
    }

    setPricing(data) {
        if (data.mode == "Pack") {
            data.sellingPrice = data.originalSPrice;
            data.purchasePrice = data.originalPPrice;
        } else if (data.mode == "Strip") {
            if (data.sheetSize != 0) {
                data.sellingPrice =
                    (data.originalSPrice / data.packSize) * data.sheetSize;
                data.purchasePrice = data.originalPPrice / data.sheetSize;
            }
        } else {
            if (data.packSize != 0) {
                data.sellingPrice = data.originalSPrice / data.packSize;
                data.purchasePrice = data.originalPPrice / data.packSize;
            }
        }
    }

    fixDigits(amt) {
        let total = 0;
        if (amt != null) {
            total = amt.toFixed(2);
        }
        return total;
    }

    clearAll(msg = false) {

        if (this.draftCartId && !this.isEditMode) {
            this.cartService.discardDraft(this.draftCartId);
            this.draftCartId = null;
        }

        this.state.selectedProfile = this.defaultCustomerName;
        this.state.customerTel = "";
        this.state.transferStoreID = "";
        this.state.chippedProfile = [];
        this.state.chippedTel = [];

        this.savedItemList = [];
        this.savedItemListAll = [];
        this.paymentList = [];

        this.telBlurred = false;
        this.submitted = false;
        (this.v$ as any).$reset(); 
        
        // Clear stored transaction branch
        BranchValidator.clearStoredTransactionBranch();

        this.item = {
            id: 0,
            profileID: this.defaultCustomerID,
            discount: 0,
            totalGrossAmt: 0,
            invoiceDiscount: 0,
            discType: "",
            totalCost: 0,
            totalBill: 0,
            totalPaid: 0,
            totalTendered: 0,
            totalChange: 0,
            totalTax1: 0,
            totalTax2: 0,
            totalTax3: 0,
            totalTax: 0,
            description: "",
            searchReceiptNo: "",
            paymentMethod: "",
            doctorDetails: "",
            patientDetails: "",
            status: "Active",
            type: this.item.type,
        };

        this.itemScanBox = "";
        this.invoiceDiscount = 0;
        this.invoiceDiscountInput = "0";
        if (msg) {
            this.toast.showSuccess("Items Removed Successfully");
        }

        localStorage.setItem("pos_items", "0");
        this.focusNext("pos-input", null, null);
    }

    clearAllManually() {
        if (this.savedItemList.length == 0) {
            this.toast.showInfo("Nothing to remove");
            return;
        }
        const answer = window.confirm("Do you want to remove those items?");
        if (answer) {
            this.clearAll(true);
        } else {
            // do nothing
        }
    }

    changeInvoiceDiscount() {
        this.invoiceDiscount = 0;
        this.invoiceDiscountInput = "0";
        this.scheduleDraftSync(); 
    }

    formatExpiryDate(d) {
        return moment(d).format("DD-MMM-YYYY");
    }

    adjustQty(type, item) {
        if (type == "minus" && item.unit > 1) {
            item.unit--;
            this.scheduleDraftSync();
        } else if (type == "add") {
            if (this.item.type == "RFD") {
                item.unit++;
                this.scheduleDraftSync();
                return;
            }

            let maxAllowed: number;
            if (item.mode === 'Pack' && item.packSize > 0) {
                maxAllowed = Math.floor(item.stockQty / item.packSize);
            } else if (item.mode === 'Strip' && item.sheetSize > 0) {
                maxAllowed = Math.floor(item.stockQty / item.sheetSize);
            } else {
                maxAllowed = item.stockQty;
            }

            if (item.unit < maxAllowed) {
                item.unit++;
                this.scheduleDraftSync();
            } else {
                this.toast.showError("Item does not have enough stock quantity");
            }
        }
    }

    adjustFreeQty(type, item) {
        if (type == "minus" && item.freeUnit > 0) {
            item.freeUnit--;
        } else if (type == "add") {
            if (item.unit + item.freeUnit < item.stockQty) {
                item.freeUnit++;
            } else {
                this.toast.showError(
                    "Item does not have enough stock quantity to provide free units"
                );
            }
        }
    }

    adjustDisc(type, item) {
        if (type == "minus" && item.itemDisc > 0) {
            item.itemDisc--;
            this.scheduleDraftSync();
        } else if (type == "add" && item.itemDisc < 100) {
            item.itemDisc++;
            this.scheduleDraftSync();
        }
    }

    clearListItem(item) {
        console.log(item);
        const answer = window.confirm("Do you want to remove this item?");
        if (answer) {
            this.savedItemList.splice(this.savedItemList.indexOf(item), 1);
            this.savedItemListAll[this.savedItemListAll.findIndex(element => element.rowID === item.rowID)].active = 0;
            this.toast.showSuccess("Item Deleted Successfully");

            if (this.savedItemList.length === 0) {
                this.discardCurrentDraft();
            } else {
                this.scheduleDraftSync();
            }

            this.focusNext("pos-input", null, null);
            // for the notification of leave page
            
        } else {
            // do nothing
        }
    }

    limitString(str) {
        if (str.length > 40) str = str.substring(0, 40) + "...";
        return str;
    }

    closePaymentScreen() {
        this.paymentDialog = false;
    }

    getProceededPayments(paymentList, isToggled) {
        this.paymentList = paymentList;
        const tenderedList = this.getTotalPaid(paymentList);
        this.item.totalPaid      = Number(tenderedList[0]);
        this.item.totalTendered  = Number(tenderedList[1]);
        this.item.totalChange    = Number(tenderedList[2]);

        const payedMethod = this.getPaymentMethod(paymentList);
        this.item.paymentMethod  = payedMethod.payMethod;
        this.item.discType       = this.discType;
        this.item.invoiceDiscount = this.invoiceDiscount;
        this.item.discount       = this.totalDiscAmount;
        this.item.totalGrossAmt  = this.totalGross;
        this.item.totalCost      = this.totalCostInventory;
        this.item.totalBill      = this.netTotal;
        this.item.totalTax1      = this.totalTax1;
        this.item.totalTax2      = this.totalTax2;
        this.item.totalTax3      = this.totalTax3;
        this.item.totalTax       = Number(this.totalTax1 + this.totalTax2 + this.totalTax3);

        this.setAccountingEntries();

        const doSave = () => {
            this.posService
                .saveItem(
                    this.item,
                    this.paymentList,
                    this.state,
                    this.savedItemList,
                    this.counterEntry,
                    true
                )
                .then((res) => {

                    if (res.alert === 'frozen') {
                        this.validationPopupMessage = res.msg;
                        this.validationPopup = true;
                        this.paymentDialog = false;
                        return;
                    }

                    if (res.alert === 'conflict') {
                        const conflictItem = this.savedItemList.find(
                            (i: any) => i.productName === res.productName
                        );
                        if (conflictItem) {
                            conflictItem.unit     = res.available;
                            conflictItem.stockQty = res.available;
                            this.scheduleDraftSync();
                        }
                        this.validationPopupMessage =
                            `Stock for "${res.productName}" changed. ` +
                            `Only ${res.available} units available. ` +
                            `Cart has been updated. Please review and try again.`;
                        this.validationPopup = true;
                        return;
                    }

                    if (res.alert == 'info') {
                        if (this.draftCartId) {
                            this.cartService.completeDraft(this.draftCartId, res.receipt_no ?? null);
                            this.draftCartId = null;
                        }

                        if (this.isEditMode && this.editCartId) {
                            this.cartService.completeCart(this.editCartId).then(() => {
                                console.log('Original token completed after direct payment');
                            });
                            this.isEditMode  = false;
                            this.editCartId  = null;
                        }

                        this.clearAll();
                        this.toast.showSuccess(res.msg);
                        this.generatePosReceipt(res);
                    }
                });

            this.paymentDialog = false;
            this.submitted     = false;
        };

        if (this.isEditMode && this.editCartId) {
            const customerData = {
                customerID:   this.item.profileID,
                customerName: this.state.selectedProfile,
                customerTel:  this.state.customerTel,
            };
            const cartData = {
                invoiceDiscount: this.invoiceDiscount,
                discType:        this.discType,
                description:     this.item.description,
                doctorDetails:   this.item.doctorDetails,
                patientDetails:  this.item.patientDetails,
            };

            this.cartService
                .syncEditCart(this.editCartId, this.savedItemList, customerData, cartData)
                .then(() => doSave())
                .catch(() => doSave());
        } else {
            doSave();
        }
    }

    generatePosReceipt(res) {
        if (res.htmlTemplate) {
            // Open a new window and write the HTML content to it
            const printWindow = window.open("", "_blank");
            if (printWindow !== null) {
                printWindow.document.open();
                printWindow.document.write(`
                        <html>
                            <head>
                                <title>Print Receipt</title>
                            </head>
                            <body onload="setTimeout(function() { window.print(); window.close(); }, 300)">
                                ${res.htmlTemplate} <!-- HTML content from the response -->
                            </body>
                        </html>
                    `);
                printWindow.document.close(); // Close the document
                // printWindow.focus(); // Focus on the new window
                // printWindow.print(); // Trigger the print dialog
                // printWindow.close(); // Close the window after printing
            }
        }
    }

    validateItems() {
        let validated = 1;
        let errorMessage = "";

        // check for duplicates
        const seen = new Set();
        this.savedItemList.filter((item) => {
            if (!(item.unit > 0)) {
                errorMessage = "Please fill all the required fields to proceed"; // if item is already in the Set, it's a duplicate
                validated = 0;
            }
            if (seen.has(item.productID + item.batchNo)) {
                errorMessage =
                    "Batch Numbers cannot be duplicate for a single product"; // if item is already in the Set, it's a duplicate
                validated = 0;
            } else {
                seen.add(item.productID + item.batchNo); // if not, add it to the Set
            }
        });

        return { status: validated, message: errorMessage };
    }

    openPaymentMethod(isFormValid) {
        let validateDetails = this.validateItems();
        // console.log(validateDetails);
        if (validateDetails.status == 0) {
            this.validationPopupMessage = validateDetails.message;
            this.validationPopup = true;
            return false;
        }

        // Validate branch before proceeding with payment
        const branchValidation = BranchValidator.validateCurrentBranch(this.branchId, this.branchName);
        if (!branchValidation.isValid) {
            this.validationPopupMessage = branchValidation.message;
            this.validationPopup = true;
            return false;
        }

        this.submitted = true;
        
        // Validate customer name and phone
        if (!(this.state.customerTel.length !== 0 || this.state.chippedTel.length !== 0 || this.state.chippedProfile.length !== 0 || this.state.selectedProfile.length !== 0)) {
            this.validationPopupMessage = "Customer Name or Phone Number is required";
            this.validationPopup = true;
            return false;
        }
        
        // if (this.state.chippedProfile.length === 0) {
        //     this.validationPopupMessage = "Customer Name is required";
        //     this.validationPopup = true;
        //     return false;
        // }
        console.log(this.state.customerTel);
        console.log(this.state.chippedTel.length);
        console.log(this.validateMobileNumber(this.state.customerTel));
        if (this.state.selectedProfile.length === 0 && this.state.chippedTel.length == 0 && !this.validateMobileNumber(this.state.customerTel)) {
            this.validationPopupMessage = "A Valid Phone Number is required";
            this.validationPopup = true;
            return false;
        }

        if ((isFormValid = true)) {
            if (this.state.customerTel === "") {
                this.isToggled = "Pos Receipt";
            } else {
                this.isToggled = "E Receipt";
            }
            this.paymentDialog = true;
            this.store.dispatch(
                ActionTypes.TOTAL_BILL,
                Number(this.fixDigits(this.netTotal))
            );
        }
    }

    get totalPaidCash() {
        let total = 0;

        this.paymentList.forEach((e) => {
            if (e.paymentType == "Cash") {
                total = total + e.transTotalAmount;
            }
        });

        return total;
    }

    get totalPaidBank() {
        let total = 0;

        this.paymentList.forEach((e) => {
            if (e.paymentType != "Cash") {
                total = total + e.transTotalAmount;
            }
        });

        return total;
    }

    getTotalPaid(paymentList) {
        let totalPaid = 0;
        let totalTendered = 0;
        let totalChange = 0;

        paymentList.forEach((e) => {
            if (e.paymentType != "Tip") {
                totalPaid = totalPaid + Number(e.transTotalAmount);
                totalTendered = totalTendered + Number(e.tendered);
                totalChange = totalChange + Number(e.change);
            }
        });

        return [totalPaid, totalTendered, totalChange];
    }

    get totalBalance() {
        return this.netTotal - this.item.totalPaid;
    }

    getPaymentMethod(paymentList) {
        let payMethod = "";
        let payMethodId = 0;

        if (paymentList.length == 0) {
            payMethod = "Pay Later";
            payMethodId = 2;
        } else if (paymentList.length == 1) {
            payMethod = paymentList[0].paymentType;
            payMethodId = paymentList[0].paymentTypeID;
        } else if (paymentList.length > 1) {
            payMethod = "Split";
            payMethodId = 0;
        }

        return { payMethod: payMethod, paymentMethodId: payMethodId };
    }

    shiftPos() {
        if (this.item.type == "TRN") {
            this.toast.showSuccess("Mode Changed Successfully");
            this.clearAll();
        }
        if (this.item.type != "TRN") {
            this.savedItemList.forEach((e) => {
                e.freeUnit = 0;
            });
        }

        if (this.item.type == "RFD") {
            window.open("/pos-refund", "_blank");
            this.item.type = "INE";
        }
        this.submitted = false;
    }

    sumUnitTotalQty(data) {
        let totalUnits = 0;

        this.savedItemList.forEach((e) => {
            if (data.stockID == e.stockID) {
                if (e.mode == "Pack") {
                    totalUnits = totalUnits + e.packSize * e.unit + e.freeUnit;
                } else if (e.mode == "Strip") {
                    totalUnits = totalUnits + e.sheetSize * e.unit + e.freeUnit;
                } else {
                    totalUnits = totalUnits + (e.freeUnit + e.unit);
                }
            }
        });

        return Number(totalUnits);
    }

    //OPEN DIALOG TO ADD NEW ITEM
    openProfileDialog() {
        this.dialogTitle = "Add New Customer";
        this.profileStatus = true;
        this.statusType = "New";
    }

    logoutCashier() {
        const answer = window.confirm(
            "Do you want to logout from Cashier Session?"
        );
        if (answer) {
            this.cashierService.logoutCashier().then((res) => {
                if (res.status_id == 1) {
                    this.toast.showInfo(res.msg);
                    this.store.dispatch(ActionTypes.CASHIER_SESSION, "");
                    router.push({ path: "/home/dashboard" });
                } else {
                    this.toast.handleResponse(res);
                    router.push({ path: "/home/dashboard" });
                }
            });

            this.paymentDialog = false;
            this.submitted = false;
        } else {
            // do nothing
        }
    }

    removeProfile(event) {
        this.state.selectedProfile = "";
        this.state.customerTel = "";
        this.state.chippedProfile = [];
        this.state.chippedTel = [];
        this.item.profileID = 1;

        // Remove only the specific profile and contact number
        const chipIndex = this.state.chippedTel.indexOf(event.value);

        if (chipIndex !== -1) {
            this.state.chippedTel.splice(chipIndex, 1);
            this.state.chippedProfile.splice(chipIndex, 1);

            // Clear additional state only if no chips remain
            if (this.state.chippedTel.length === 0) {
                this.state.selectedProfile = "";
                this.state.customerTel = "";
            }
        }
    }

    updateProfilerStatus(res) {
        this.profileStatus = false;
        if (res[0] == "load") {
            this.state.selectedProfile = res[1].account_title;
            this.state.customerTel = res[1].contact_no;
            this.item.profileID = res[1].id;
            const profileInfo = {value: res[1]};
            this.removeProfile(profileInfo);
            this.saveProfile(profileInfo);
            this.focusNext('custDesc', null, null);
            this.scheduleDraftSync();
        }
    }

    redirectHome() {
        this.screenState.fullscreen = false;
        router.replace({ path: "/home/dashboard" });
    }

    isExpiryDateCritical(date: { expiry_date: string }): string {
        const today = new Date();
        const expiryDate = new Date(date.expiry_date);
        const diffTime = expiryDate.getTime() - today.getTime(); // Use getTime() to get milliseconds
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return "expired-date";
        } else if (diffDays <= 31 && diffDays > 0) {
            return "expired-date-month";
        } else if (diffDays <= 92 && diffDays >= 31) {
            return "expired-date-3months";
        } else if (diffDays <= 183 && diffDays >= 92) {
            return "expired-date-6months";
        } else {
            return "";
        }
    }

    setAccountingEntries() {
        this.counterEntry = [];

        if (this.item.type == "INE") {
            if (this.totalBalance == 0) {
                if (this.totalPaidCash > 0) {
                    this.counterEntry.push({
                        accountID: 2,
                        accountHead: "Cash in hand",
                        amount: this.totalPaidCash,
                        type: "Debit",
                    });
                }

                if (this.totalPaidBank > 0) {
                    this.counterEntry.push({
                        accountID: 8,
                        accountHead: "Cash at bank",
                        amount: this.totalPaidBank,
                        type: "Debit",
                    });
                }
            } else if (this.totalBalance == this.netTotal) {
                this.counterEntry.push({
                    accountID: 4,
                    accountHead: "Accounts receivable",
                    amount: this.netTotal,
                    type: "Debit",
                });
            } else if (this.totalBalance > 0 && this.item.totalPaid > 0) {
                if (this.totalPaidCash > 0) {
                    this.counterEntry.push({
                        accountID: 2,
                        accountHead: "Cash in hand",
                        amount: this.totalPaidCash,
                        type: "Debit",
                    });
                }

                if (this.totalPaidBank > 0) {
                    this.counterEntry.push({
                        accountID: 8,
                        accountHead: "Cash at bank",
                        amount: this.totalPaidBank,
                        type: "Debit",
                    });
                }

                this.counterEntry.push({
                    accountID: 4,
                    accountHead: "Accounts receivable",
                    amount: this.totalBalance,
                    type: "Debit",
                });
            }



            this.counterEntry.push({
                accountID: 48,
                accountHead: "Sales Revenue",
                amount: this.totalGross - this.totalDiscAmount,
                type: "Credit",
            });



            //ADDING TAXES
            if (this.totalTax1 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[0].accountID,
                    accountHead: this.taxNames[0].accountHead,
                    amount: this.totalTax1,
                    type: "Credit",
                });
            }

            if (this.totalTax2 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[1].accountID,
                    accountHead: this.taxNames[1].accountHead,
                    amount: this.totalTax2,
                    type: "Credit",
                });
            }

            if (this.totalTax3 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[2].accountID,
                    accountHead: this.taxNames[2].accountHead,
                    amount: this.totalTax3,
                    type: "Credit",
                });
            }
        } else if (this.item.type == "RFD") {
            this.counterEntry.push({
                accountID: 48,
                accountHead: "Sales Revenue",
                amount: this.totalGross - this.totalDiscAmount,
                type: "Debit",
            });



            //ADDING TAXES
            if (this.totalTax1 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[0].accountID,
                    accountHead: this.taxNames[0].accountHead,
                    amount: this.totalTax1,
                    type: "Debit",
                });
            }

            if (this.totalTax2 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[1].accountID,
                    accountHead: this.taxNames[1].accountHead,
                    amount: this.totalTax2,
                    type: "Debit",
                });
            }

            if (this.totalTax3 != 0) {
                this.counterEntry.push({
                    accountID: this.taxNames[2].accountID,
                    accountHead: this.taxNames[2].accountHead,
                    amount: this.totalTax3,
                    type: "Debit",
                });
            }

            if (this.totalBalance == 0) {
                if (this.totalPaidCash > 0) {
                    this.counterEntry.push({
                        accountID: 2,
                        accountHead: "Cash in hand",
                        amount: this.totalPaidCash,
                        type: "Credit",
                    });
                }

                if (this.totalPaidBank > 0) {
                    this.counterEntry.push({
                        accountID: 8,
                        accountHead: "Cash at bank",
                        amount: this.totalPaidBank,
                        type: "Credit",
                    });
                }
            } else if (this.totalBalance == this.netTotal) {
                this.counterEntry.push({
                    accountID: 5,
                    accountHead: "Accounts payable",
                    amount: this.netTotal,
                    type: "Credit",
                });
            } else if (this.totalBalance > 0 && this.item.totalPaid > 0) {
                if (this.totalPaidCash > 0) {
                    this.counterEntry.push({
                        accountID: 2,
                        accountHead: "Cash in hand",
                        amount: this.totalPaidCash,
                        type: "Credit",
                    });
                }

                if (this.totalPaidBank > 0) {
                    this.counterEntry.push({
                        accountID: 8,
                        accountHead: "Cash at bank",
                        amount: this.totalPaidBank,
                        type: "Credit",
                    });
                }

                this.counterEntry.push({
                    accountID: 5,
                    accountHead: "Accounts payable",
                    amount: this.totalBalance,
                    type: "Credit",
                });
            }


        } else {
            //TRANSFER ENTRY NO NEED
        }
    }

    get currency() {
        return this.store.getters.getCurrency;
    }

    checkPaymentPermission() {
        const paymentOptions = ["Card Payment", "Cash Payment", "Pay Later"];

        const store = useStore();

        const pL = JSON.parse(store.getters.gerUserPermissions);

        for (let option of paymentOptions) {
            const filtered = pL.filter((e) => e.name === option);
            let res = false;

            if (filtered.length > 0) {
                res = true;
            }
            if (res && option == "Card Payment") {
                this.cardPaymentOption = true;
            } else if (res && option == "Bank Transfer Payment") {
                this.bankTransferPaymentOption = true;
            } else if (res && option == "Cash Payment") {
                this.cashPaymentOption = true;
            } else if (res && option == "Pay Later") {
                this.payLaterOption = true;
            }
        }
    }

    addToQueue() {
        const customerData = {
            customerID:   this.item.profileID,
            customerName: this.state.selectedProfile,
            customerTel:  this.state.customerTel,
        };
        const cartData = {
            invoiceDiscount: this.invoiceDiscount,
            discType:        this.discType,
            description:     this.item.description,
            doctorDetails:   this.item.doctorDetails,
            patientDetails:  this.item.patientDetails,
        };

        if (this.isEditMode && this.editCartId) {
            this.cartService
                .reissueToken(this.editCartId, this.savedItemList, customerData, cartData)
                .then((res) => {
                    if (res.alert === 'frozen') {
                        this.validationPopupMessage = res.msg;
                        this.validationPopup = true;
                        this.paymentDialog = false;
                        return;
                    }

                    if (res.alert === 'info') {
                        this.toast.showSuccess(
                            'Token ' + String(res.display_number).padStart(3, '0') + ' reissued successfully'
                        );
                        this.printTokenSlip(res.slip_html);
                        this.paymentDialog = false;
                        this.isEditMode    = false;
                        this.editCartId    = null;
                        this.draftCartId   = null;  
                        this.clearAll();
                    } else {
                        this.toast.showError(res.msg || 'Failed to reissue token');
                    }
                });
            return;
        }

        clearTimeout(this.draftSyncTimer);  

        this.cartService.upsertDraftCart(
            this.draftCartId,
            this.savedItemList,
            customerData,
            cartData
        ).then((res) => {

            if (res.alert === 'frozen') {
                this.toast.showError(res.msg);
                return;
            }

            if (!res || res.alert !== 'info') {
                this.toast.showError(res?.msg || 'Failed to save cart');
                return;
            }
            const cartId = res.cart_id;
            this.draftCartId = cartId;
            return this.cartService.tokeniseDraft(cartId);
        }).then((res: any) => {
            if (!res) return;

            if (res.alert === 'frozen') {
                this.validationPopupMessage = res.msg;
                this.validationPopup = true;
                this.paymentDialog = false;
                return;
            }

            if (res.alert === 'info') {
                this.toast.showSuccess(
                    'Token #' + String(res.display_number).padStart(3, '0') + ' issued successfully'
                );
                this.printTokenSlip(res.slip_html);
                this.paymentDialog = false;
                this.draftCartId   = null;
                this.clearAll();
            } else {
                this.toast.showError(res.msg || 'Failed to issue token');
            }
        }).catch((err) => {
            console.error('addToQueue error:', err);
            this.toast.showError('An error occurred while issuing token');
        });
    }

    tokeniseDraft() {
        this.cartService.tokeniseDraft(this.draftCartId).then((res) => {

            if (res.alert === 'frozen') {
                this.toast.showError(res.msg);
                return;
            }

            if (res.alert === 'info') {
                this.toast.showSuccess('Token issued: ' + res.token_number);
                if (res.slip_html) {
                    this.printTokenSlip(res.slip_html); 
                }
                this.draftCartId = null;
                this.clearAll();                         
            } else {
                this.toast.showError(res.msg || 'Failed to issue token');
            }
        });
    }

    discardCurrentDraft() {
        if (this.draftCartId && !this.isEditMode) {
            this.cartService.discardDraft(this.draftCartId).then(() => {
                this.draftCartId = null;
            });
        }
    }

    printTokenSlip(slipHtml: string) {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(`
                <html>
                    <head><title>Token Slip</title></head>
                    <body onload="setTimeout(function(){
                        window.print(); window.close();
                    }, 400)">
                        ${slipHtml}
                    </body>
                </html>
            `);
            printWindow.document.close();
        }
    }

    loadCartForEdit(cartId: number) {
        this.isEditMode = true;
        this.isLoadingCart = true; 

        this.cartService.loadForEdit(cartId).then((res) => {
            if (res.alert !== 'info') {
                this.toast.showError(res.msg || 'Failed to load cart');
                return;
            }

            const cart  = res.cart;
            const items = res.items;

            this.state.selectedProfile = cart.customer_name || this.defaultCustomerName;
            this.state.customerTel     = cart.customer_tel  || '';
            this.item.profileID        = cart.customer_id   || this.defaultCustomerID;
            this.item.description      = cart.description   || '';
            this.item.doctorDetails    = cart.doctor_details  || '';
            this.item.patientDetails   = cart.patient_details || '';
            this.invoiceDiscount       = Number(cart.invoice_discount) || 0;
            this.invoiceDiscountInput  = this.invoiceDiscount.toString();
            this.discType              = cart.disc_type || 'Flat';

            if (cart.customer_name) {
                this.state.chippedProfile = [cart.customer_name];
            }
            if (cart.customer_tel) {
                this.state.chippedTel = [cart.customer_tel];
                this.delayedChippedTel = false;
            }

            this.submitted = false;
           (this.v$ as any).$reset();

            this.adjustedItems = [];
            this.savedItemList = [];
            this.savedItemListAll = [];

            items.forEach((item: any, index: number) => {
                const originalQty  = Number(item.requested_qty);
                const availableQty = Number(item.available_qty);

                let maxUnitQty = availableQty;
                if (item.mode === 'Pack' && item.pack_size > 0) {
                    maxUnitQty = Math.floor(availableQty / item.pack_size);
                } else if (item.mode === 'Strip' && item.strip_size > 0) {
                    maxUnitQty = Math.floor(availableQty / item.strip_size);
                }

                const adjustedQty = Math.min(originalQty, maxUnitQty);
                const wasAdjusted = adjustedQty < originalQty;

                if (wasAdjusted) {
                    this.adjustedItems.push(
                        `${item.product_name}: ${originalQty} to ${adjustedQty} (only ${maxUnitQty} available)`
                    );
                }

                const rowID = index + 1;

                this.savedItemList.push({
                    rowID:          rowID,
                    mode:           item.mode,
                    stockID:        item.stock_id,
                    productID:      item.product_id,
                    productName:    item.product_name,
                    generic:        '',
                    itemDescription: '',
                    unit:           this.isEditMode ? adjustedQty   : (adjustedQty > 0 ? adjustedQty : 1),
                    totalUnit:      0,
                    stockQty:       availableQty,
                    freeUnit:       0,
                    supplierBonus:  0,
                    batchNo:        item.batch_no,
                    packSize:       Number(item.pack_size),
                    sheetSize:      Number(item.strip_size),
                    purchasePrice:  Number(item.purchase_price),
                    originalPPrice: Number(item.purchase_price),
                    originalSPrice: Number(item.selling_price),
                    sellingPrice:   Number(item.selling_price),
                    mrp:            Number(item.mrp),
                    brandName:      '',
                    sectorName:     '',
                    categoryName:   '',
                    productType:    '',
                    expiryDate:     item.expiry_date,
                    itemDisc:       Number(item.item_disc),
                    tax1:           Number(item.tax_1),
                    tax2:           Number(item.tax_2),
                    tax3:           Number(item.tax_3),
                    subTotal:       0,
                });

                this.savedItemListAll.push({ active: 1, rowID });
            });

            localStorage.setItem('pos_items', this.savedItemList.length > 0 ? '1' : '0');

            if (this.adjustedItems.length > 0) {
                setTimeout(() => {
                    this.validationPopupMessage =
                        'The following items were adjusted to match available stock:\n\n' +
                        this.adjustedItems.join('\n') +
                        '\n\nPlease review before reissuing the token.';
                    this.validationPopup = true;
                }, 600);
            } else {
                this.toast.showSuccess('Cart loaded. All quantities are still available.');
            }

            this.focusNext('pos-input', null, null);
            console.log('loadCartForEdit called', cartId);

            setTimeout(() => {
                this.isLoadingCart = false;  
            }, 150);
        });
    }

    scheduleDraftSync() {
        if (this.isEditMode) return;
        if (this.savedItemList.length === 0) return;   

        clearTimeout(this.draftSyncTimer);
        this.draftSyncTimer = setTimeout(() => {
            this.syncDraft();
        }, 800);
    }

    syncDraft() {
        if (this.isEditMode) return;
        if (this.savedItemList.length === 0) return;  

        const customerData = {
            customerID:   this.item.profileID,          
            customerName: this.state.selectedProfile,  
            customerTel:  this.state.customerTel,       
        };
        const cartData = {
            invoiceDiscount: this.invoiceDiscount,
            discType:        this.discType,
            description:     this.item.description,     
            doctorDetails:   this.item.doctorDetails,   
            patientDetails:  this.item.patientDetails,  
        };

        this.cartService.upsertDraftCart(
            this.draftCartId,
            this.savedItemList,                         
            customerData,
            cartData
        ).then((res) => {
            if (res && res.cart_id) {
                this.draftCartId = res.cart_id;
            }
        });
    }

    onlyLetters(event) {
        if (!/^[a-zA-Z ]$/.test(event.key)) {
            event.preventDefault();
        }
    }

    onlyNumbers(event: KeyboardEvent) {
        const char = event.key;
        if (!/^[0-9]$/.test(char)) {
            event.preventDefault();
        }
    }

    get isWalkInCustomer(): boolean {
        const hasChippedProfile = this.state.chippedProfile.length > 0 && 
                                this.state.chippedProfile[0] !== this.defaultCustomerName;
        const hasTypedName = this.state.selectedProfile.length > 0 && 
                            this.state.selectedProfile !== this.defaultCustomerName;
        const hasTypedPhone = this.state.customerTel.length > 0;

        if (hasChippedProfile || hasTypedName || hasTypedPhone) {
            return false;
        }

        return true;
    }
}
